import React, { useEffect, useMemo, useState } from "react";
import {
    Button, Card, Segmented, Input, Select, Switch, Space, Typography, Table, Tooltip, Empty, Alert,
} from "antd";
import {
    PlusOutlined, DeleteOutlined, ArrowUpOutlined, ArrowDownOutlined, SaveOutlined, TeamOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import SideBar from "../../components/SideBar";
import NavBar from "../../components/NavBar";
import Footer from "../../components/Footer";
import { rightsService } from "../../services/rightsService";
import {
    TYPE_DOC_SIGNATAIRE, getSignatairesConfig, saveSignatairesConfig,
} from "../../hooks/useSignataires";

const { Title, Text } = Typography;
const ACCENT = "#0891b2";
const RIGHT_ECRITURE = "HSI118";

const DOC_OPTIONS = [
    { label: "Ordre de dépense", value: TYPE_DOC_SIGNATAIRE.ORDRE_DEPENSE },
    { label: "Fiche de demande de règlement", value: TYPE_DOC_SIGNATAIRE.FICHE_DEMANDE_REGLEMENT },
    { label: "Quittance d'indemnité", value: TYPE_DOC_SIGNATAIRE.QUITTANCE_INDEMNITE },
];

const SOURCE_NOM_OPTIONS = [
    { label: "Aucun (espace à signer)", value: "AUCUN" },
    { label: "Responsable de l'organisme", value: "RESPONSABLE_ORGANISME" },
    { label: "Nom fixe", value: "FIXE" },
];

const SignatairesDocuments = () => {
    const canEdit = rightsService(RIGHT_ECRITURE);
    const [typeDoc, setTypeDoc] = useState(TYPE_DOC_SIGNATAIRE.ORDRE_DEPENSE);
    const [lignes, setLignes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const fetchConfig = (doc) => {
        setLoading(true);
        getSignatairesConfig(doc)
            .then((res) => {
                const list = Array.isArray(res.data) ? res.data : [];
                setLignes(list.map((l, i) => ({ key: l.codeId || `k${i}`, ...l })));
            })
            .catch(() => toast.error("Erreur lors du chargement de la configuration."))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        fetchConfig(typeDoc);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [typeDoc]);

    const update = (idx, patch) =>
        setLignes((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));

    const move = (idx, dir) => {
        setLignes((prev) => {
            const j = idx + dir;
            if (j < 0 || j >= prev.length) return prev;
            const copy = [...prev];
            [copy[idx], copy[j]] = [copy[j], copy[idx]];
            return copy;
        });
    };

    const remove = (idx) => setLignes((prev) => prev.filter((_, i) => i !== idx));

    const add = () =>
        setLignes((prev) => [
            ...prev,
            { key: `new-${Date.now()}`, codeId: null, titre: "", sourceNom: "AUCUN", nomFixe: "", afficheParDefaut: true },
        ]);

    const save = () => {
        const clean = lignes
            .map((l, i) => ({
                codeId: l.codeId || null,
                titre: (l.titre || "").trim(),
                ordre: i + 1,
                sourceNom: l.sourceNom || "AUCUN",
                nomFixe: l.sourceNom === "FIXE" ? (l.nomFixe || "").trim() : null,
                afficheParDefaut: l.afficheParDefaut !== false,
            }))
            .filter((l) => l.titre);
        if (!clean.length) {
            toast.warning("Ajoutez au moins un signataire avec un titre.");
            return;
        }
        setSaving(true);
        saveSignatairesConfig(typeDoc, clean)
            .then((res) => {
                const list = Array.isArray(res.data) ? res.data : [];
                setLignes(list.map((l, i) => ({ key: l.codeId || `k${i}`, ...l })));
                toast.success("Signataires enregistrés.");
            })
            .catch((err) => {
                console.error(err);
                toast.error(err?.response?.status === 403
                    ? "Habilitation requise (HSI118)."
                    : "Erreur lors de l'enregistrement.");
            })
            .finally(() => setSaving(false));
    };

    const columns = useMemo(() => [
        {
            title: "Ordre", key: "ordre", width: 90, align: "center",
            render: (_, __, idx) => (
                <Space size={0}>
                    <Tooltip title="Monter"><Button type="text" size="small" icon={<ArrowUpOutlined />} disabled={!canEdit || idx === 0} onClick={() => move(idx, -1)} /></Tooltip>
                    <Tooltip title="Descendre"><Button type="text" size="small" icon={<ArrowDownOutlined />} disabled={!canEdit || idx === lignes.length - 1} onClick={() => move(idx, 1)} /></Tooltip>
                </Space>
            ),
        },
        {
            title: "Titre du signataire", dataIndex: "titre", key: "titre",
            render: (v, _, idx) => (
                <Input value={v} disabled={!canEdit} placeholder="ex : Directeur des Opérations"
                    onChange={(e) => update(idx, { titre: e.target.value })} />
            ),
        },
        {
            title: "Nom affiché", dataIndex: "sourceNom", key: "sourceNom", width: 260,
            render: (v, rec, idx) => (
                <Space direction="vertical" style={{ width: "100%" }} size={4}>
                    <Select value={v || "AUCUN"} disabled={!canEdit} style={{ width: "100%" }}
                        options={SOURCE_NOM_OPTIONS} onChange={(val) => update(idx, { sourceNom: val })} />
                    {v === "FIXE" && (
                        <Input value={rec.nomFixe} disabled={!canEdit} placeholder="Nom (ex : Yao Contant DJEKET)"
                            onChange={(e) => update(idx, { nomFixe: e.target.value })} />
                    )}
                </Space>
            ),
        },
        {
            title: "Affiché par défaut", dataIndex: "afficheParDefaut", key: "afficheParDefaut", width: 140, align: "center",
            render: (v, _, idx) => (
                <Switch checked={v !== false} disabled={!canEdit} onChange={(checked) => update(idx, { afficheParDefaut: checked })} />
            ),
        },
        {
            title: "", key: "actions", width: 60, align: "center",
            render: (_, __, idx) => (
                <Tooltip title="Supprimer"><Button type="text" danger size="small" icon={<DeleteOutlined />} disabled={!canEdit} onClick={() => remove(idx)} /></Tooltip>
            ),
        },
    ], [canEdit, lignes.length]);

    return (
        <div className="layout-wrapper layout-content-navbar">
            <div className="layout-container">
                <SideBar currentMenu="PARAMETRES_SINISTRES" />
                <div className="layout-page">
                    <NavBar />
                    <div className="content-wrapper">
                        <div className="container-xxl flex-grow-1 container-p-y">
                            <Card bordered={false} style={{ borderRadius: 14, marginBottom: 16, background: "linear-gradient(135deg,#ecfeff,#f8fafc)" }} bodyStyle={{ padding: 20 }}>
                                <Space align="center" size={14}>
                                    <div style={{ width: 44, height: 44, borderRadius: 12, display: "grid", placeItems: "center", background: ACCENT, color: "#fff", fontSize: 22 }}>
                                        <TeamOutlined />
                                    </div>
                                    <div>
                                        <Title level={4} style={{ margin: 0 }}>Signataires des documents de règlement</Title>
                                        <Text type="secondary">Titres, ordre et nom affichés sur l'ordre de dépense, la fiche de demande et la quittance — propres à votre organisme.</Text>
                                    </div>
                                </Space>
                            </Card>

                            {!canEdit && (
                                <Alert type="info" showIcon style={{ marginBottom: 16 }}
                                    message="Lecture seule"
                                    description="Le paramétrage des signataires requiert l'habilitation HSI118." />
                            )}

                            <Card bordered style={{ borderRadius: 14 }} bodyStyle={{ padding: 16 }}>
                                <Space wrap style={{ marginBottom: 16, justifyContent: "space-between", width: "100%" }}>
                                    <Segmented options={DOC_OPTIONS} value={typeDoc} onChange={setTypeDoc} />
                                    <Space>
                                        <Button icon={<PlusOutlined />} onClick={add} disabled={!canEdit}>Ajouter un signataire</Button>
                                        <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={save} disabled={!canEdit} style={{ background: ACCENT }}>
                                            Enregistrer
                                        </Button>
                                    </Space>
                                </Space>

                                <Table
                                    rowKey="key"
                                    dataSource={lignes}
                                    columns={columns}
                                    loading={loading}
                                    pagination={false}
                                    size="middle"
                                    locale={{ emptyText: <Empty description="Aucun signataire — ajoutez-en un" /> }}
                                />
                                <Text type="secondary" style={{ display: "block", marginTop: 12 }}>
                                    L'ordre du tableau = l'ordre d'affichage (gauche → droite) sur le document. Un signataire
                                    « non affiché par défaut » peut être activé au cas par cas à l'impression d'un règlement.
                                </Text>
                            </Card>
                        </div>
                        <Footer />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SignatairesDocuments;
